import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { groups, posts, users } from '../data/mockData';

const GroupPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const GroupIcon = styled.div`
  font-size: 3rem;
  margin-right: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GroupDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const GroupStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const JoinButton = styled.button`
  background: ${({ theme, joined }) => joined ? theme.colors.success : theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Tab = styled.button`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.secondary};
  border-bottom: 2px solid ${({ theme, active }) => active ? theme.colors.primary : 'transparent'};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const PostCard = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled(Link)`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PostTime = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PostTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PostDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const PostMedia = styled.img`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PostActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.hover};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

function GroupPage() {
  const { groupId } = useParams();
  const [activeTab, setActiveTab] = useState('posts');
  const [joined, setJoined] = useState(false);

  // Find the group by ID
  const group = groups.find(g => g.id === parseInt(groupId));

  // If group doesn't exist, show error
  if (!group) {
    return (
      <GroupPageContainer>
        <EmptyState>
          <EmptyStateText>Group not found</EmptyStateText>
          <Link to="/discover">Back to Group Discovery</Link>
        </EmptyState>
      </GroupPageContainer>
    );
  }

  // Get posts for this group
  const groupPosts = posts.filter(post => post.group && post.group.id === group.id);

  return (
    <GroupPageContainer>
      <GroupHeader>
        <GroupIcon>{group.icon}</GroupIcon>
        <GroupInfo>
          <GroupName>{group.name}</GroupName>
          <GroupDescription>{group.description}</GroupDescription>
          <GroupStats>
            <StatItem>
              <StatValue>{group.members.toLocaleString()}</StatValue>
              <StatLabel>Members</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{groupPosts.length}</StatValue>
              <StatLabel>Posts</StatLabel>
            </StatItem>
          </GroupStats>
        </GroupInfo>
        <JoinButton joined={joined} onClick={() => setJoined(!joined)}>
          {joined ? 'Joined' : 'Join Group'}
        </JoinButton>
      </GroupHeader>

      <TabsContainer>
        <Tab
          active={activeTab === 'posts'}
          onClick={() => setActiveTab('posts')}
        >
          Posts
        </Tab>
        <Tab
          active={activeTab === 'members'}
          onClick={() => setActiveTab('members')}
        >
          Members
        </Tab>
        <Tab
          active={activeTab === 'about'}
          onClick={() => setActiveTab('about')}
        >
          About
        </Tab>
      </TabsContainer>

      {activeTab === 'posts' && (
        <PostsContainer>
          {groupPosts.length > 0 ? (
            groupPosts.map(post => (
              <PostCard key={post.id}>
                <PostHeader>
                  <UserAvatar src={post.user.avatar} alt={post.user.name} />
                  <UserInfo>
                    <UserName to={`/profile/${post.user.username}`}>{post.user.name}</UserName>
                    <PostTime>{post.timeAgo}</PostTime>
                  </UserInfo>
                </PostHeader>
                <PostTitle>{post.title}</PostTitle>
                <PostDescription>{post.description}</PostDescription>
                {post.media && <PostMedia src={post.media} alt={post.title} />}
                <PostActions>
                  <ActionButton>
                    👍 {post.likes}
                  </ActionButton>
                  <ActionButton>
                    💬 {post.comments}
                  </ActionButton>
                  <ActionButton>
                    Share
                  </ActionButton>
                </PostActions>
              </PostCard>
            ))
          ) : (
            <EmptyState>
              <EmptyStateText>No posts in this group yet</EmptyStateText>
              <Link to="/create-post">Create the first post</Link>
            </EmptyState>
          )}
        </PostsContainer>
      )}

      {activeTab === 'members' && (
        <EmptyState>
          <EmptyStateText>Member list coming soon</EmptyStateText>
        </EmptyState>
      )}

      {activeTab === 'about' && (
        <div>
          <h2>About {group.name}</h2>
          <p>{group.description}</p>
          <p>Category: {group.category}</p>
        </div>
      )}
    </GroupPageContainer>
  );
}

export default GroupPage;
